import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWsEvent } from '@/hooks/use-websocket';
import { getChecklistToday } from '@/services/pos-api';
const REFRESH_MS = 60000; // statuses change with the clock (due times, hourly slots)
/**
 * Today's employee checklist for the POS dashboard: loads on mount, refreshes
 * on `checklist.updated` events and every minute, and exposes an Attention
 * item when anything is overdue or missed.
 */
export function useChecklistToday(enabled = true) {
    const [day, setDay] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const refresh = useCallback(async () => {
        if (!enabled)
            return;
        setLoading(true);
        try {
            const data = await getChecklistToday();
            // Never let an unexpected payload take down the dashboard.
            if (!data || !Array.isArray(data.tasks) || !data.summary)
                throw new Error('Checklist unavailable');
            setDay(data);
            setError(null);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load checklist');
        }
        finally {
            setLoading(false);
        }
    }, [enabled]);
    useEffect(() => {
        refresh();
        if (!enabled)
            return;
        const timer = window.setInterval(refresh, REFRESH_MS);
        return () => window.clearInterval(timer);
    }, [enabled, refresh]);
    useWsEvent('checklist.updated', () => {
        refresh();
    });
    const attentionItem = useMemo(() => {
        if (!day || day.summary.overdue === 0)
            return null;
        const late = day.tasks
            .filter((t) => t.items.some((i) => i.status === 'OVERDUE' || i.status === 'MISSED'))
            .map((t) => t.title);
        const n = day.summary.overdue;
        return {
            id: `checklist-overdue-${day.date}-${n}`,
            kind: 'checklist_overdue',
            severity: n >= 3 ? 'red' : 'amber',
            title: `${n} checklist item${n === 1 ? '' : 's'} overdue`,
            detail: late.slice(0, 3).join(' · ') + (late.length > 3 ? ` +${late.length - 3} more` : ''),
            linkHref: '#checklist',
            createdAt: new Date().toISOString(),
        };
    }, [day]);
    return { day, error, loading, refresh, attentionItem };
}
